import type { Lake, SpotDetail, SpotRatings } from "@divespots/contracts";
import { pickLocale } from "@divespots/contracts";
import {
  ArrowDownToLine,
  Compass,
  Gauge,
  MapPin,
  Navigation,
  Pencil,
  Ruler,
  SquareParking,
  Waves,
} from "lucide-react";
import { useId, useMemo } from "react";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "../components/Button.tsx";
import { DiveEntry } from "../divegate/DiveEntry.tsx";
import { List, Row } from "../components/List.tsx";
import { ObjectIcon } from "../components/ObjectIcon.tsx";
import { SegmentedControl } from "../components/SegmentedControl.tsx";
import type { Detent } from "../components/Sheet.tsx";
import { formatCoordinate, formatMetres, formatResolvedDepth, pickText } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import {
  OfflineSaveButton,
  OfflineSaveStatus,
  useOfflineSave,
} from "../offline/OfflineSaveControl.tsx";
import { CoursePanel } from "./nav/CoursePanel.tsx";
import { useCourseSelection } from "./nav/useCourse.ts";
import type { CourseObject } from "./nav/useCourse.ts";
import { SightingActions, SightingSummary } from "./ObjectSightings.tsx";
import { SpotHeader } from "./SpotHeader.tsx";

const TEXT_SECTIONS = [
  "description",
  "access",
  "parking",
  "entry",
  "hazards",
  "restrictions",
  "facilities",
] as const;
const RATING_KEYS = [
  "fish",
  "walls",
  "caves",
  "wrecksSculptures",
  "current",
  "surroundings",
] as const;

interface Props {
  spot: SpotDetail;
  lake?: Lake | undefined;
  detent: Detent;
  /** Injectable for tests; defaults to the current time. */
  now?: Date | undefined;
  titleRef?: Ref<HTMLHeadingElement> | undefined;
  view?: "2d" | "3d";
  onViewChange?: (view: "2d" | "3d") => void;
  /** Moderators and admins get a link to the editor. */
  editHref?: string | undefined;
  /** Who is looking: divers can report sightings and suggest changes, anonymous visitors are pointed to the login. */
  viewer?: "anonymous" | "diver" | "staff" | undefined;
  /** Objects that are outside the loaded 3D tile (flagged, with a hint to enlarge the radius). */
  outsideIds?: ReadonlySet<string> | undefined;
}

/** Header + 2D/3D control + facts, objects, text sections and ratings (empty ones are hidden). */
export function SpotDetailView({
  spot,
  lake,
  detent,
  now,
  titleRef,
  view = "2d",
  onViewChange = () => undefined,
  editHref,
  outsideIds,
  viewer,
}: Props) {
  const { t } = useTranslation();
  const locale = useLocale();
  const uid = useId();
  const noteId = `${uid}-note`;
  const offline = useOfflineSave(spot);

  const courseSel = useCourseSelection();
  const courseObjects = useMemo<CourseObject[]>(
    () =>
      spot.objects.map((o) => ({
        id: o.id,
        name: pickLocale(o.name, locale) ?? "",
        type: o.type,
        location: o.location,
        depthM: o.depth.valueM,
        approx: o.depth.source === "computed",
      })),
    [spot.objects, locale],
  );
  const courseStart = courseObjects.find((o) => o.id === courseSel.startId);

  const anyComputed = spot.objects.some((o) => o.depth.source === "computed");
  const ratings = RATING_KEYS.filter((k) => spot.ratings[k] !== null);
  const texts = TEXT_SECTIONS.flatMap((key) => {
    const text = pickText(spot[key], locale);
    return text ? [{ key, ...text }] : [];
  });
  const waterLevel = lake
    ? t("facts.waterLevelValue", { value: formatMetres(lake.waterLevelM, locale, 1) })
    : null;

  return (
    <>
      <SpotHeader spot={spot} detent={detent} now={now} titleRef={titleRef} />

      <div className="spot-sheet__body">
        <div className="grid gap-2 justify-items-start">
          <div className="spot-actions">
            <SegmentedControl
              aria-label={t("spot.view")}
              value={view}
              onChange={onViewChange}
              options={[
                { value: "2d", label: t("spot.view2d") },
                {
                  value: "3d",
                  label: t("spot.view3d"),
                  disabled: !spot.hasBathy,
                  describedBy: noteId,
                },
              ]}
            />
            {offline ? <OfflineSaveButton model={offline} /> : null}
            {viewer === "diver" || viewer === "staff" ? (
              <DiveEntry lakeSlug={spot.lakeSlug} spotSlug={spot.slug} />
            ) : null}
            {editHref ? (
              <Link
                className="ds-btn ds-btn--tinted ds-btn--icon"
                to={editHref}
                aria-label={t("admin.edit")}
                title={t("admin.edit")}
              >
                <Pencil aria-hidden="true" strokeWidth={1.75} />
              </Link>
            ) : null}
          </div>
          {!spot.hasBathy ? (
            <p id={noteId} className="ds-text-footnote text-label-secondary m-0">
              {t("spot.noBathy")}
            </p>
          ) : null}
          {offline ? <OfflineSaveStatus model={offline} /> : null}
          {viewer === "diver" ? (
            <div className="flex flex-wrap gap-2">
              <Link
                className="ds-btn ds-btn--tinted"
                to={`/${locale}/konto/vorschlagen/spot/${spot.slug}`}
              >
                {t("suggest.editSpotButton")}
              </Link>
              <Link
                className="ds-btn ds-btn--tinted"
                to={`/${locale}/konto/vorschlagen/objekt/${spot.slug}`}
              >
                {t("suggest.objectButton")}
              </Link>
            </div>
          ) : null}
          {viewer === "anonymous" ? (
            <p className="ds-text-footnote text-label-secondary m-0">
              <Link to={`/${locale}/login`}>{t("suggest.loginHint")}</Link>
            </p>
          ) : null}
        </div>

        {
          <>
            <section aria-labelledby={`${uid}-facts`}>
              <h3 id={`${uid}-facts`} className="ds-text-title-2 spot-sheet__section-title">
                {t("facts.title")}
              </h3>
              <List>
                {spot.difficulty ? (
                  <Row
                    icon={<Gauge />}
                    title={t("facts.difficulty")}
                    value={t(`difficulty.${spot.difficulty}`)}
                  />
                ) : null}
                {spot.maxDepthM !== null ? (
                  <Row
                    icon={<ArrowDownToLine />}
                    title={t("facts.maxDepth")}
                    value={formatMetres(spot.maxDepthM, locale, 0)}
                    depth
                  />
                ) : null}
                {spot.waterBody ? (
                  <Row icon={<Waves />} title={t("facts.waterBody")} value={spot.waterBody} />
                ) : null}
                {spot.locality ? (
                  <Row icon={<MapPin />} title={t("facts.locality")} value={spot.locality} />
                ) : null}
                {waterLevel ? (
                  <Row icon={<Ruler />} title={t("facts.waterLevel")} value={waterLevel} depth />
                ) : null}
                <Row
                  icon={<Navigation />}
                  title={t("facts.entry")}
                  value={
                    <span className="ds-text-coordinate">{formatCoordinate(spot.location)}</span>
                  }
                />
                {spot.parkingLocation ? (
                  <Row
                    icon={<SquareParking />}
                    title={t("facts.parking")}
                    value={
                      <span className="ds-text-coordinate">
                        {formatCoordinate(spot.parkingLocation)}
                      </span>
                    }
                  />
                ) : null}
              </List>
            </section>

            {spot.objects.length > 0 ? (
              <section aria-labelledby={`${uid}-objects`}>
                <h3 id={`${uid}-objects`} className="ds-text-title-2 spot-sheet__section-title">
                  {t("objects.title")}
                </h3>
                {view === "2d" ? <CoursePanel objects={courseObjects} variant="list" /> : null}
                <List>
                  {spot.objects.map((o) => {
                    const depth = formatResolvedDepth(o.depth, locale);
                    const name = pickLocale(o.name, locale) ?? t(`objects.type.${o.type}`);
                    const spoken =
                      o.depth.valueM === null
                        ? undefined
                        : `${o.depth.source === "computed" ? `${t("objects.approx")} ` : ""}${formatMetres(o.depth.valueM, locale, 1)}`;
                    return (
                      <Row
                        key={o.id}
                        tone="lake"
                        icon={<ObjectIcon type={o.type} />}
                        title={
                          outsideIds?.has(o.id) ? (
                            <>
                              {name}
                              <span className="ds-text-footnote text-label-secondary block">
                                {t("objects.outside")}
                              </span>
                            </>
                          ) : (
                            name
                          )
                        }
                        depth={depth !== null}
                        value={depth ?? t("objects.noData")}
                        valueLabel={spoken}
                        extra={
                          <>
                            <SightingSummary object={o} />
                            {spot.objects.length > 1 ? (
                              courseStart && !courseSel.endId && courseStart.id !== o.id ? (
                                <Button
                                  variant="plain"
                                  icon={<Compass aria-hidden="true" strokeWidth={1.75} />}
                                  aria-label={t("course.rowTo", {
                                    from: courseStart.name || t(`objects.type.${courseStart.type}`),
                                    name,
                                  })}
                                  onClick={() => courseSel.setEnd(o.id)}
                                >
                                  {t("course.asDestination")}
                                </Button>
                              ) : (
                                <Button
                                  variant="plain"
                                  icon={<Compass aria-hidden="true" strokeWidth={1.75} />}
                                  aria-label={t("course.rowFrom", { name })}
                                  onClick={() => courseSel.setStart(o.id)}
                                >
                                  {t("course.action")}
                                </Button>
                              )
                            ) : null}
                            {viewer === "diver" || viewer === "staff" ? (
                              <SightingActions object={o} slug={spot.slug} />
                            ) : null}
                            {viewer === "diver" ? (
                              <Link
                                className="ds-btn ds-btn--plain"
                                to={`/${locale}/konto/vorschlagen/objekt/${spot.slug}/${o.id}`}
                              >
                                {t("suggest.editObjectButton")}
                              </Link>
                            ) : null}
                          </>
                        }
                      />
                    );
                  })}
                </List>
                {anyComputed ? (
                  <p className="ds-text-footnote text-label-secondary mt-2 mb-0">
                    {t("objects.provenance")}
                  </p>
                ) : null}
              </section>
            ) : null}

            {texts.map(({ key, value, lang }) => (
              <section key={key} aria-labelledby={`${uid}-${key}`}>
                <h3 id={`${uid}-${key}`} className="ds-text-title-2 spot-sheet__section-title">
                  {t(`sections.${key}`)}
                </h3>
                <p lang={lang} className="ds-text-body m-0 whitespace-pre-line">
                  {value}
                </p>
              </section>
            ))}

            {ratings.length > 0 ? (
              <section aria-labelledby={`${uid}-ratings`}>
                <h3 id={`${uid}-ratings`} className="ds-text-title-2 spot-sheet__section-title">
                  {t("ratings.title")}
                </h3>
                <List>
                  {ratings.map((k) => (
                    <RatingRow key={k} name={k} value={spot.ratings[k] as number} />
                  ))}
                </List>
              </section>
            ) : null}
          </>
        }
      </div>
    </>
  );
}

function RatingRow({ name, value }: { name: keyof SpotRatings; value: number }) {
  const { t } = useTranslation();
  const label = t(`ratings.${name}`);
  const text = t("ratings.value", { label, value });
  return (
    <li className="ds-row">
      <span className="ds-row__title" aria-hidden="true">
        {label}
      </span>
      <span role="img" aria-label={text} className="rating-dots">
        {[1, 2, 3, 4, 5].map((n) => (
          <span key={n} className="rating-dot" data-on={n <= value} />
        ))}
      </span>
    </li>
  );
}
